import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from './Modal';
import LoadingState from './LoadingState';
import { IndeterminateProgressBar } from './ProgressBar';
import ErrorMessage from './ErrorMessage';
import { formatDateTime as formatDateTimeUtil } from '../utils/dateFormatter';


export default function Settings() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { isAdmin } = useAuth();
  const [multiplier, setMultiplier] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [isMultiplierModalOpen, setIsMultiplierModalOpen] = useState(false);
  const [multiplierForm, setMultiplierForm] = useState({ multiplier: '1' });
  const [shopifySettings, setShopifySettings] = useState({
    min_stock_weeks_coverage: '4',
    sales_history_months: '6'
  });
  const [syncReport, setSyncReport] = useState<any>(null);
  const [isSyncReportModalOpen, setIsSyncReportModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  useEffect(() => {
    loadMultiplier();
    loadShopifySettings();
  }, []);

  const loadShopifySettings = async () => {
    try {
      const { data } = await api.get('/shopify/settings');
      setShopifySettings({
        min_stock_weeks_coverage: data.MIN_STOCK_WEEKS_COVERAGE || '4',
        sales_history_months: data.SALES_HISTORY_MONTHS || '6'
      });
    } catch (err) {
      console.error('Error loading shopify settings', err);
    }
  };

  const handleShopifySync = async () => {
    try {
      setSyncing(true);
      setSyncError(null);
      // Sincronizar últimos 6 meses por defecto si se pulsa manualmente para asegurar datos
      // O solo 1 mes para ser más rápido. El backend usa default 24h si no se pasa nada.
      // Pero si cambiamos la configuración de meses, tal vez queramos resincronizar más.
      // Vamos a dejarlo por defecto (24h) para sync rápida, o preguntar?
      // Mejor sync rápida. Para carga inicial usaremos un botón especial o parámetros.
      const { data } = await api.post('/shopify/sync', {});
      
      // Guardar el informe completo
      setSyncReport(data);
      setIsSyncReportModalOpen(true);
      
      const fetched = data.fetched ?? data.syncedOrders ?? 0;
      const imported = data.imported ?? data.syncedOrders ?? 0;
      const msg = `Sincronización completada. Recuperadas: ${fetched}, Importadas: ${imported}. Recálculo de stock actualizado.`;
      showSuccess(msg);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Error en sincronización';
      setSyncError(errorMsg);
      showError(errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveShopifySettings = async () => {
    try {
      setLoading(true);
      await api.put('/shopify/settings', {
        min_stock_weeks_coverage: shopifySettings.min_stock_weeks_coverage,
        sales_history_months: shopifySettings.sales_history_months
      });
      showSuccess('Configuración de cálculo actualizada');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error guardando configuración');
    } finally {
      setLoading(false);
    }
  };


  const loadMultiplier = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/min-stock-multiplier');
      setMultiplier(response.data.multiplier);
    } catch (err: any) {
      showError('Error al cargar multiplicador');
    } finally {
      setLoading(false);
    }
  };




  const handleUpdateMultiplier = async () => {
    // SISTEMA DE PERMISOS DESHABILITADO - Todos los usuarios pueden cambiar el multiplicador

    try {
      const multiplierValue = parseFloat(multiplierForm.multiplier);
      if (isNaN(multiplierValue) || multiplierValue < 0.1 || multiplierValue > 10) {
        showError('El multiplicador debe estar entre 0.1 y 10');
        return;
      }

      await api.put('/settings/min-stock-multiplier', {
        multiplier: multiplierValue,
      });

      showSuccess('Multiplicador actualizado correctamente');
      setMultiplier(multiplierValue);
      window.dispatchEvent(
        new CustomEvent('multiplier-updated', {
          detail: { multiplier: multiplierValue },
        })
      );
      setIsMultiplierModalOpen(false);
      setMultiplierForm({ multiplier: '1' });
      loadMultiplier();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar multiplicador');
    }
  };


  if (loading && multiplier === 1) {
    return (
      <div className="tab-content">
        <LoadingState type="table" message="Cargando configuración..." />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <h2>Configuración</h2>


      {/* Multiplicador de Mínimos */}
      <div className="table-container" style={{ marginTop: '1.5rem', padding: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Multiplicador de Stock Mínimo</h3>
        <div className="flex-between">
          <div>
            <p><strong>Multiplicador actual:</strong> {multiplier}x</p>
            <small style={{ color: 'var(--text-secondary)' }}>
              Este multiplicador afecta a todos los stock mínimos configurados
            </small>
          </div>
            <button
            className="btn btn-primary"
            onClick={() => {
              setMultiplierForm({ multiplier: multiplier.toString() });
              setIsMultiplierModalOpen(true);
            }}
            disabled={!isAdmin}
          >
            Cambiar Multiplicador
          </button>
        </div>
      </div>

      {/* Integración Shopify */}
      <div className="table-container" style={{ marginTop: '1.5rem', padding: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Integración Shopify & Cálculo de Stock</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
          <div>
            <h4 style={{ marginBottom: '1rem' }}>Sincronización</h4>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              La sincronización automática se ejecuta diariamente a las 09:00 UTC.
              Usa este botón para importar ventas recientes y recalcular mínimos inmediatamente.
            </p>
            <button 
              className="btn btn-primary"
              onClick={handleShopifySync}
              disabled={syncing || loading}
            >
              {syncing ? 'Sincronizando...' : '🔄 Sincronizar Ahora'}
            </button>
            {syncing && (
              <div style={{ marginTop: '1rem' }}>
                <IndeterminateProgressBar
                  label="Sincronizando con Shopify..."
                  variant="primary"
                  size="medium"
                />
              </div>
            )}
            {syncError && (
              <div style={{ marginTop: '1rem' }}>
                <ErrorMessage
                  message={syncError}
                  variant="error"
                  action={{
                    label: "Reintentar",
                    onClick: handleShopifySync
                  }}
                  onDismiss={() => setSyncError(null)}
                />
              </div>
            )}
          </div>

          <div>
             <h4 style={{ marginBottom: '1rem' }}>Parámetros de Cálculo</h4>
             <div className="input-group">
               <label className="input-label">Meses de Historial de Ventas</label>
               <input 
                 type="number"
                 className="input"
                 value={shopifySettings.sales_history_months}
                 onChange={(e) => setShopifySettings({...shopifySettings, sales_history_months: e.target.value})}
               />
               <small style={{ color: 'var(--text-secondary)' }}>
                 Periodo hacia atrás para calcular la media de consumo diario.
               </small>
             </div>
             <div className="input-group">
               <label className="input-label">Semanas de Cobertura (Stock Mínimo + Downstream)</label>
               <input 
                 type="number"
                 className="input"
                 value={shopifySettings.min_stock_weeks_coverage}
                 onChange={(e) => setShopifySettings({...shopifySettings, min_stock_weeks_coverage: e.target.value})}
               />
               <small style={{ color: 'var(--text-secondary)' }}>
                 Semanas totales de stock a mantener (cubre tiempo de producción y stock de seguridad).
               </small>
             </div>
             <button 
               className="btn btn-secondary"
               onClick={handleSaveShopifySettings}
               disabled={loading}
               style={{ marginTop: '0.5rem' }}
             >
               Guardar Parámetros
             </button>
          </div>
        </div>
      </div>






      {/* Modal Cambiar Multiplicador */}
      <Modal
        isOpen={isMultiplierModalOpen}
        onClose={() => {
          setIsMultiplierModalOpen(false);
          setMultiplierForm({ multiplier: multiplier.toString() });
        }}
        title="Cambiar Multiplicador de Stock Mínimo"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsMultiplierModalOpen(false);
                setMultiplierForm({ multiplier: multiplier.toString() });
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleUpdateMultiplier}
              data-modal-default="true"
            >
              Actualizar
            </button>
          </>
        }
      >
        <div className="input-group">
          <label className="input-label">Nuevo Multiplicador *</label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            max="10"
            className="input"
            value={multiplierForm.multiplier}
            onChange={(e) => setMultiplierForm({ ...multiplierForm, multiplier: e.target.value })}
            required
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
            Valor entre 0.1 y 10 (ej: 0.5 = 50%, 1 = 100%, 2 = 200%)
          </small>
        </div>
      </Modal>


      {/* Modal Informe de Sincronización Shopify */}
      <Modal
        isOpen={isSyncReportModalOpen}
        onClose={() => setIsSyncReportModalOpen(false)}
        title="📊 Informe de Sincronización con Shopify"
        size="large"
        footer={
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', width: '100%' }}>
            <button
              className="btn btn-primary"
              onClick={() => setIsSyncReportModalOpen(false)}
              data-modal-default="true"
            >
              Cerrar
            </button>
          </div>
        }
      >
        {syncReport && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Resumen General */}
            <div style={{ 
              marginBottom: '2rem', 
              padding: '1.5rem', 
              backgroundColor: syncReport.success ? 'rgba(0, 128, 96, 0.1)' : 'rgba(255, 59, 48, 0.1)',
              borderRadius: '8px',
              border: `1px solid ${syncReport.success ? 'rgba(0, 128, 96, 0.3)' : 'rgba(255, 59, 48, 0.3)'}`
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {syncReport.success ? '✅' : '❌'} Estado: {syncReport.success ? 'Sincronización Exitosa' : 'Error en Sincronización'}
              </h3>
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{syncReport.message}</p>
            </div>

            {syncReport.summary && (
              <>
                {/* Estadísticas de Órdenes */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                    📦 Órdenes de Shopify
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Órdenes Recuperadas</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                        {syncReport.summary.ordersFetched}
                      </div>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Órdenes Canceladas</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                        {syncReport.summary.ordersCancelled}
                      </div>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Sin SKU</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                        {syncReport.summary.ordersWithoutSKU}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Estadísticas de Items */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                    📋 Items Procesados
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Items Encontrados</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                        {syncReport.summary.itemsFound}
                      </div>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Items Importados</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--accent)' }}>
                        {syncReport.summary.itemsImported}
                      </div>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Items Omitidos</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                        {syncReport.summary.itemsSkipped}
                      </div>
                    </div>
                    {syncReport.summary.itemsFilteredByPrice !== undefined && syncReport.summary.itemsFilteredByPrice > 0 && (
                      <div style={{ padding: '1rem', backgroundColor: 'rgba(128, 128, 128, 0.1)', borderRadius: '8px', border: '1px solid rgba(128, 128, 128, 0.3)' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Filtrados por Precio &lt; $50</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                          {syncReport.summary.itemsFilteredByPrice}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                          (Servicios, no productos físicos)
                        </div>
                      </div>
                    )}
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Items con Errores</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: syncReport.summary.itemsWithErrors > 0 ? 'var(--error)' : 'var(--text-secondary)' }}>
                        {syncReport.summary.itemsWithErrors}
                      </div>
                    </div>
                    {syncReport.summary.historicalSales !== undefined && (
                      <div style={{ padding: '1rem', backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Ventas Históricas</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--warning)' }}>
                          {syncReport.summary.historicalSales}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                          (Sin descontar stock)
                        </div>
                      </div>
                    )}
                    {syncReport.summary.stockDiscounted !== undefined && (
                      <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Con Stock Descontado</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--accent)' }}>
                          {syncReport.summary.stockDiscounted}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Estadísticas de SKUs */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                    🏷️ SKUs Procesados
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Total SKUs</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                        {syncReport.summary.skusProcessed}
                      </div>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Con BOM</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--accent)' }}>
                        {syncReport.summary.skusWithBOM}
                      </div>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Sin BOM</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: syncReport.summary.skusWithoutBOM > 0 ? 'var(--warning)' : 'var(--text-secondary)' }}>
                        {syncReport.summary.skusWithoutBOM}
                      </div>
                    </div>
                  </div>
                </div>

                {/* SKUs Pendientes de BOM */}
                {syncReport.summary.pendingBOMSkus && syncReport.summary.pendingBOMSkus.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem', color: 'var(--warning)' }}>
                      ⚠️ SKUs Pendientes de BOM ({syncReport.summary.pendingBOMSkus.length} familias)
                    </h3>
                    <div style={{ padding: '1rem', backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                      <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        Se detectaron {syncReport.summary.pendingBOMSkus.length} familias de SKU que no tienen BOM asignado. 
                        Puedes añadirlas ahora para definir sus componentes manualmente.
                      </p>
                      <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '1rem' }}>
                        <table style={{ width: '100%', fontSize: '0.875rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Código Familia</th>
                              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Cantidad Total</th>
                              <th style={{ textAlign: 'left', padding: '0.5rem' }}>SKUs Detectados</th>
                              <th style={{ textAlign: 'center', padding: '0.5rem' }}>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {syncReport.summary.pendingBOMSkus.map((pending: any, idx: number) => (
                              <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontWeight: 'bold' }}>
                                  {pending.family_code}
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  {pending.total_quantity}
                                </td>
                                <td style={{ padding: '0.5rem' }}>
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                    {pending.detected_skus.slice(0, 3).join(', ')}
                                    {pending.detected_skus.length > 3 && ` (+${pending.detected_skus.length - 3} más)`}
                                  </div>
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                  <button
                                    className="btn btn-small btn-primary"
                                    onClick={() => {
                                      // Navegar a la pestaña de BOM y abrir el formulario de asignación
                                      window.location.hash = '#bom';
                                      // Disparar evento para abrir el modal de asignación
                                      setTimeout(() => {
                                        window.dispatchEvent(new CustomEvent('openBOMAssign', { 
                                          detail: { familyCode: pending.family_code } 
                                        }));
                                      }, 100);
                                      setIsSyncReportModalOpen(false);
                                    }}
                                  >
                                    Definir BOM
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            // Navegar a la pestaña de BOM
                            window.location.hash = '#bom';
                            setIsSyncReportModalOpen(false);
                          }}
                        >
                          Ver Todos en BOM
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Estadísticas de Unidades */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                    📊 Unidades Totales
                  </h3>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Total de Unidades Vendidas</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--accent)' }}>
                      {syncReport.summary.totalUnits.toLocaleString('es-ES')}
                    </div>
                  </div>
                </div>

                {/* Acciones Realizadas */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem' }}>
                    ⚙️ Acciones Realizadas
                  </h3>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyle: 'none' }}>
                      <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>✅</span>
                        <span>Órdenes recuperadas desde Shopify API</span>
                      </li>
                      <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>✅</span>
                        <span>Items filtrados y validados (duplicados omitidos)</span>
                      </li>
                      <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>✅</span>
                        <span>Ventas importadas a la base de datos</span>
                      </li>
                      <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{syncReport.summary.stockDiscounted > 0 ? '✅' : '⏸️'}</span>
                        <span>
                          Stock descontado de ubicaciones de acabado 
                          {syncReport.summary.stockDiscounted !== undefined && ` (${syncReport.summary.stockDiscounted} ventas recientes)`}
                        </span>
                      </li>
                      {syncReport.summary.historicalSales > 0 && (
                        <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>📋</span>
                          <span>
                            {syncReport.summary.historicalSales} ventas históricas registradas sin descontar stock (más de 7 días)
                          </span>
                        </li>
                      )}
                      <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>✅</span>
                        <span>Piedras descontadas del almacén (solo ventas recientes)</span>
                      </li>
                      <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{syncReport.summary.stockRecalculated ? '✅' : '⏸️'}</span>
                        <span>Stock mínimo recalculado dinámicamente</span>
                      </li>
                      <li style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>✅</span>
                        <span>Fecha de última sincronización actualizada</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Errores si los hay */}
                {syncReport.details?.errors && syncReport.details.errors.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ marginBottom: '1rem', borderBottom: '2px solid var(--border-color)', paddingBottom: '0.5rem', color: 'var(--error)' }}>
                      ⚠️ Errores Encontrados
                    </h3>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '1rem', backgroundColor: 'rgba(255, 59, 48, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 59, 48, 0.3)' }}>
                      <table style={{ width: '100%', fontSize: '0.75rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <th style={{ textAlign: 'left', padding: '0.5rem' }}>SKU</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem' }}>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncReport.details.errors.slice(0, 20).map((error: any, idx: number) => (
                            <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{error.sku}</td>
                              <td style={{ padding: '0.5rem', color: 'var(--error)' }}>{error.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {syncReport.details.errors.length > 20 && (
                        <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          ... y {syncReport.details.errors.length - 20} errores más
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

    </div>
  );
}

